
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'react-apollo';
import {Grid, Dialog} from '@material-ui/core';
import {HighlightOff} from '@material-ui/icons';
import {autobind} from 'core-decorators';
import ReactMarkdown from 'react-markdown';
import {formatMoney, getVariationOptions} from 'core/utils';
import Text from 'components/text';
import cartQuery from 'graphql/queries/cart.gql';
import addToCartMutation from 'graphql/mutations/carts/add-to-cart.gql';
import {Form, Field, Input, Select, Toggle} from 'components/forms';
import Button from 'components/button';
import styles from './styles.css';

@graphql(addToCartMutation, {
    props: ({mutate}) => ({
        onSubmit: variables => mutate({
            variables,
            refetchQueries: [{
                query: cartQuery,
            }],
        }),
    }),
})
@autobind
export default class ProductDetailModal extends Component {
    static propTypes = {
        product: PropTypes.object,
        handlOnCloseModal: PropTypes.func.isRequired,
    };

    state = {
        errors: '',
    }

    validateStock(quantity, {variation: selectedVariation}) {
        const variation = this.props.product.variations.find(variation => variation.id === selectedVariation);
        return quantity <= variation.stock || 'No hay stock disponible.';
    }

    async handleSubmit(values) {
        this.setState({loading: true});
        await this.props.onSubmit(values);
        this.setState({loading: false, showSuccess: true});
        this.timeout = setTimeout(() => this.setState({showSuccess: false}), 5000);
    }

    renderStock(product) {
        const {variationType, variations} = product;

        const renderAmount = n => `${n} ${n === 1 ? 'unidad' : 'unidades'}`;

        if (variations.length === 1) {
            return <Text>{renderAmount(variations[0].stock)}</Text>;
        }

        const stock = variations
            .map(variation => `${variationType} ${variation.name} (${renderAmount(variation.stock)})`)
            .join(', ');

        return <Text>{stock}</Text>;
    }

    render() {
        const {product, handlOnCloseModal} = this.props;
        return (
            <Dialog maxWidth='lg' open={product && product.id}>
                <Grid container spacing={2} className={styles.root}>
                    <Grid item xs={6} >
                        <img src={product.thumbnail} className={styles.image} alt='foto de producto' />
                    </Grid>
                    <Grid item xs={6}>
                        <Grid container spacing={2} justify='flex-start'>
                            <Grid item xs={11}>
                                <Text variant='h2'>{product.name}</Text>
                            </Grid>
                            <Grid item xs={1} className={styles.close}>
                                <div onClick={handlOnCloseModal}>
                                    <HighlightOff fontSize='large' />
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <Text bold='bolder' variant='h1' >{formatMoney(product, 'price')}</Text>
                            </Grid>
                            {product.description && (
                                <Grid item xs={12}>
                                    <Text variant='h4' bold='bolder'>Descripción</Text>
                                    <Text variant='h3'>{product.description}</Text>
                                </Grid>
                            )}
                            {product.features.map(feature => (
                                <Grid key={product.id} item xs={12}>
                                    <Text variant='h4' bold='bolder'>Caracteristicas</Text>
                                    <Text key={feature.value}>{`${feature.value} ${feature.name}`}</Text>
                                </Grid>
                            ))}
                            {product.variationType && (
                                <Grid item xs={12}>
                                    <span>{product.variations.map(variation => variation.name).join(', ')}</span>
                                </Grid>
                            )}
                            {product.variations.length > 0 && (
                                <Grid item xs={12}>
                                    <Text variant='h4' bold='bolder'>Stock</Text>
                                    {this.renderStock(product)}
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <ReactMarkdown className={styles.sizesTable} source={product.sizes} />
                            </Grid>
                        </Grid>
                        <Form
                            fields={[
                                {key: 'variation', initial: getVariationOptions(product)[0].value},
                                {key: 'quantity', initial: '1', validators: [this.validateStock]},
                                {key: 'withOrganizationLogo', initial: false, required: false},
                            ]}
                            errors={this.state.errors}
                            onSubmit={this.handleSubmit}>
                            {({getFieldProps, renderErrors, isValid, values}) => (
                                <Grid className={styles.formContainer} spacing={2} container justify='flex-start'>
                                    <Grid item xs={6}>
                                        <Field label='Cantidad' vertical>
                                            <Input className={styles.input} type='number' min={1} {...getFieldProps('quantity')} />
                                            {renderErrors('quantity')}
                                        </Field>
                                    </Grid>
                                    {product.variations.length > 1 && (
                                        <Grid item xs={6}>
                                            <Field label='variación' vertical>
                                                <div>
                                                    <Select
                                                        className={styles.variation}
                                                        theme='outline'
                                                        options={getVariationOptions(product)}
                                                        {...getFieldProps('variation')}
                                                    />
                                                    {renderErrors('variation')}
                                                </div>
                                            </Field>
                                        </Grid>
                                    )}
                                    {product.allowOrganizationLogo && (
                                        <Grid item xs={12}>
                                            <Field label='Con logo' horizontal>
                                                <Toggle
                                                    type='checkbox'
                                                    {...getFieldProps('withOrganizationLogo')}
                                                    disabled={!(values.quantity >= 15)}
                                                />
                                                {renderErrors('withOrganizationLogo')}
                                            </Field>
                                            <Text variant='h4' color='textSecondary'>
                                                El logo está disponible para pedidos de más de 15 unidades.
                                            </Text>
                                        </Grid>
                                    )}
                                    {renderErrors('__all__')}
                                    <Grid item xs={12}>
                                        {product.isPreOrder && <p className={styles.preOrder}>Fecha de entrega a confirmar</p>}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button type='submit' fullWidth theme='yellow' disabled={!isValid() || this.state.loading}>
                                            {this.state.loading ? 'AGREGANDO PRODUCTO...' : 'AGREGAR AL CARRITO'}
                                        </Button>
                                    </Grid>
                                    {this.state.showSuccess && (
                                        <Grid item xs={12}>
                                            <div className={styles.success}>
                                                <i className={styles.check} /> Producto agregado al carrito
                                            </div>
                                        </Grid>
                                    )}
                                    <div className={styles.disclaimer}>
                                        <Text>Precios expresados en dólares estadounidenses.</Text>
                                        <Text>
                                            Imágenes no contractuales. Son solo a modo ilustrativo y pueden diferir del producto real.
                                        </Text>
                                    </div>
                                </Grid>
                            )}
                        </Form>
                    </Grid>

                </Grid>
            </Dialog>
        );
    }
}




// WEBPACK FOOTER //
// ./components/product-detail-modal/index.js